/**
 * Locale registry.
 *
 * Deliberately data-driven rather than a hardcoded union type: the live list
 * comes from the `locales` table via /api/locales.php, so commissioning a new
 * language is an admin insert plus a JSON drop into public/locales/<code>/ —
 * no code change and no rebuild.
 *
 * The list below is only the offline fallback (unreachable API, or a database
 * that predates the i18n migration). It intentionally mirrors the seed rows in
 * migrate.php.
 *
 * Locale codes are BCP-47 ("en", "fr-CA", "es-MX"). Regional variants matter
 * here: Quebec construction vocabulary differs from France French, so the
 * shipped French is fr-CA rather than a generic fr.
 */

export interface LocaleDef {
  /** BCP-47 tag. The identity used everywhere: URLs, storage, DB rows. */
  code: string;
  /** Name in English — used by the admin UI. */
  englishName: string;
  /** Endonym — what the visitor sees in the switcher. */
  nativeName: string;
  /** Next locale to try when a key is missing. NULL ends the chain. */
  fallbackCode: string | null;
}

/**
 * The source language. Dictionaries are authored in this locale, it terminates
 * every fallback chain, and it is what the UI renders if everything else fails.
 */
export const SOURCE_LOCALE = 'en';

/** Offline fallback registry. The server list wins whenever it is reachable. */
export const BUILTIN_LOCALES: LocaleDef[] = [
  { code: 'en',    englishName: 'English',          nativeName: 'English',  fallbackCode: null },
  { code: 'fr-CA', englishName: 'French (Canada)',  nativeName: 'Français', fallbackCode: 'en' },
  { code: 'es-MX', englishName: 'Spanish (Mexico)', nativeName: 'Español',  fallbackCode: 'en' },
];

/**
 * i18next namespaces, split by area so a visitor downloads the wizard strings
 * without also pulling the account and admin-adjacent copy. Keep in sync with
 * the files in public/locales/<code>/.
 */
export const NAMESPACES = ['common', 'wizard', 'results', 'auth', 'account'] as const;
export type Namespace = (typeof NAMESPACES)[number];
export const DEFAULT_NAMESPACE: Namespace = 'common';

/** Storage key for the visitor's explicit language choice. */
export const LOCALE_STORAGE_KEY = 'trusscore_locale';

// ---------------------------------------------------------------------------
// Pseudo-locale (QA only)
// ---------------------------------------------------------------------------

/**
 * A generated dictionary where every English value is accented, bracketed and
 * padded — see scripts/make-pseudo-locale.js. Switching to it makes every
 * translated string visibly change, so anything still in plain English on
 * screen is a string the extraction pass missed.
 *
 * Never offered unless explicitly requested, so it cannot reach real users.
 */
export const PSEUDO_LOCALE: LocaleDef = {
  code: 'en-XA',
  englishName: 'Pseudo (QA)',
  nativeName: '⟦Pseudo⟧',
  fallbackCode: SOURCE_LOCALE,
};

/**
 * True only while `?pseudo=1` is in the URL.
 *
 * Deliberately NOT sticky. An earlier version remembered the opt-in for the
 * session, which meant the QA locale kept appearing in the switcher long after
 * anyone wanted it, with no obvious way to get rid of it. Reading the URL every
 * time makes it self-clearing: drop the parameter and it is gone.
 *
 * Also not tied to NODE_ENV — the build worth testing is a production build,
 * which is exactly where a NODE_ENV check would disable it.
 */
export function pseudoLocaleEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('pseudo') === '1';
  } catch {
    return false;
  }
}

/** Append the pseudo-locale to a registry when this session opted in. */
export function withPseudoLocale(registry: LocaleDef[]): LocaleDef[] {
  if (!pseudoLocaleEnabled()) return registry;
  if (registry.some(l => l.code === PSEUDO_LOCALE.code)) return registry;
  return [...registry, PSEUDO_LOCALE];
}

/**
 * Shape-check a raw registry entry from the API. Anything malformed is dropped
 * rather than allowed to poison the chain resolver.
 */
export function isLocaleDef(raw: unknown): raw is LocaleDef {
  if (!raw || typeof raw !== 'object') return false;
  const o = raw as Record<string, unknown>;
  return (
    typeof o.code === 'string' && o.code.trim() !== '' &&
    typeof o.englishName === 'string' &&
    typeof o.nativeName === 'string' &&
    (o.fallbackCode === null || typeof o.fallbackCode === 'string')
  );
}

/** Normalize a server payload into a usable registry, falling back if empty. */
export function normalizeLocales(raw: unknown): LocaleDef[] {
  if (!Array.isArray(raw)) return BUILTIN_LOCALES;
  const cleaned = raw.filter(isLocaleDef).map(l => ({
    ...l,
    code: l.code.trim(),
    fallbackCode: l.fallbackCode?.trim() || null,
  }));
  return cleaned.length > 0 ? cleaned : BUILTIN_LOCALES;
}

/**
 * Resolve the fallback chain for a locale, e.g. 'fr-CA' → ['fr-CA', 'fr', 'en'].
 *
 * Walks `fallbackCode` links and always terminates at SOURCE_LOCALE. Guards
 * against a cycle (an admin pointing fr → es and es → fr would otherwise hang),
 * because the registry is admin-editable data and cannot be assumed acyclic.
 */
export function resolveChain(code: string, registry: LocaleDef[]): string[] {
  const byCode = new Map(registry.map(l => [l.code, l]));
  const chain: string[] = [];
  const seen = new Set<string>();

  let cursor: string | null = code;
  while (cursor && !seen.has(cursor)) {
    seen.add(cursor);
    chain.push(cursor);
    cursor = byCode.get(cursor)?.fallbackCode ?? null;
  }

  if (!chain.includes(SOURCE_LOCALE)) chain.push(SOURCE_LOCALE);
  return chain;
}

/**
 * Pick the best supported locale for a set of preferred tags — used to match
 * `navigator.languages` against the registry.
 *
 * Matching is progressively looser: exact tag, then same language with a
 * different region (a browser asking for fr-FR gets fr-CA rather than English),
 * then the bare language. Returns null when nothing matches, so the caller can
 * apply its own default rather than being handed a silent guess.
 */
export function matchLocale(preferred: readonly string[], registry: LocaleDef[]): string | null {
  const codes = registry.map(l => l.code);
  const lower = new Map(codes.map(c => [c.toLowerCase(), c]));

  for (const raw of preferred) {
    const tag = raw.trim().toLowerCase();
    if (!tag) continue;

    const exact = lower.get(tag);
    if (exact) return exact;

    const lang = tag.split('-')[0];
    const sameLanguage = codes.find(c => c.toLowerCase().split('-')[0] === lang);
    if (sameLanguage) return sameLanguage;
  }

  return null;
}

/** True when `code` is present and active in the registry. */
export function isSupportedLocale(code: string | null | undefined, registry: LocaleDef[]): boolean {
  if (!code) return false;
  return registry.some(l => l.code === code);
}
